/**
 * Generating a reference-data schema + its first data document from a form (ADR-127 D2).
 *
 * WHY A GENERATOR AND NOT A TEMPLATE
 * ---------------------------------
 * ADR-125 D12 forbids anything hand-written per schema or per version, anywhere. Every schema
 * in the fleet so far was hand-authored JSON — this is the module that makes that unnecessary,
 * and it must not become the thing D12 rules out. So it takes a description and emits the
 * document; it holds no per-schema knowledge and no list of known sets.
 *
 * WHY IT EMITS TWO DOCUMENTS
 * -------------------------
 * A schema with no data document is VALID to CI but INVISIBLE to the Data area — the registry
 * is keyed by data documents, so `classify` returns nothing for it. Emitting the schema alone
 * would produce an artifact the operator cannot then see or edit. The pair is the unit, which
 * is why the propose is a changeset (ADR-134 D1).
 *
 * WHAT IT DELIBERATELY WILL NOT DO
 * -------------------------------
 * It does not decide whether the draft is VALID. That answer comes from the canonical Python
 * validator via `POST /api/data-references/validate-draft`; a second opinion computed here
 * would be the drift ADR-129 D1 exists to prevent. This module's job is to produce a
 * well-FORMED candidate and to refuse the inputs that cannot produce one at all.
 */

/** The layer-2 vocabulary, CLOSED at six (ADR-129 D4). Read out of the canonical validator
 *  rather than from prose: `grep -oE '"x-[a-z-]+"' scripts/validate-reference-data.py`. A
 *  seventh keyword is a core schema-engine change, not a form option. */
export const LAYER2_KEYWORDS = [
  'x-key',
  'x-provenance',
  'x-unit',
  'x-deprecated',
  'x-unknown-sentinel',
  'x-valid-time',
] as const

/**
 * `object` makes the record a TREE rather than a row.
 *
 * Everything below this form already handled nesting: layer 1 is JSON Schema
 * 2020-12 ([ADR-125] D2's layer table names "nesting, arrays of objects"), the
 * reference grammar is a dotted path of arbitrary depth, `_descend` in
 * validate_data_refs walks it, and the picker's `schema_digest` already emits
 * `address.city` as an offer. Only this builder was flat.
 *
 * That gap was not cosmetic. A form offering scalars only makes an author model
 * `address_city`/`address_state` as sibling fields to fit it — and under
 * [ADR-125] D4 a published version is IMMUTABLE, so that flattening becomes the
 * contract. Un-flattening later is not an edit; it is v2 plus a migration of
 * every consumer that spells `address_city`.
 */
export type FieldType = 'string' | 'number' | 'integer' | 'boolean' | 'object'

export interface DraftField {
  name: string
  type: FieldType
  required: boolean
  description?: string
  /** R12 — the unit a number is in. A bare number whose unit lives in a comment is the
   *  failure this annotation exists for. */
  unit?: string
  /** R14 — deprecated WITH a date. A strike-through alone says "don't use this" without
   *  saying since when, which is the half an operator needs to judge urgency. */
  deprecatedOn?: string
  /** R6 — the value that means "nobody has answered this yet", kept type-preserving by a
   *  generated oneOf so every record that DOES know stays its declared type. */
  unknownSentinel?: string
  /**
   * An `object` field's children. Arbitrary depth — the schema language, the
   * validator and the pickers all recurse, so the only limit is legibility.
   */
  fields?: DraftField[]
}

/**
 * Every SCALAR leaf, as the dotted path a consumer spells:
 * `address.city`, not `address`.
 *
 * The key field, the provenance fields and the first-records columns are all
 * about leaves, because an object is not a value: `x-key: "address"` would name
 * something that cannot be a key, and a spreadsheet cell cannot hold a subtree.
 */
export function leafPaths(fields: DraftField[], prefix = ''): string[] {
  const out: string[] = []
  for (const f of fields) {
    if (!f.name.trim()) continue
    const path = prefix ? `${prefix}.${f.name}` : f.name
    if (f.type === 'object') out.push(...leafPaths(f.fields ?? [], path))
    else out.push(path)
  }
  return out
}

/** Read a dotted path out of a record. */
export function readPath(row: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>(
    (node, seg) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[seg] : undefined),
    row)
}

/** Write a dotted path into a record, creating the objects on the way down. */
export function writePath(
  row: Record<string, unknown>, path: string, value: unknown,
): Record<string, unknown> {
  const segments = path.split('.')
  const out = { ...row }
  let node: Record<string, unknown> = out
  for (const seg of segments.slice(0, -1)) {
    const existing = node[seg]
    const child = existing && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...(existing as Record<string, unknown>) }
      : {}
    node[seg] = child
    node = child
  }
  node[segments[segments.length - 1]] = value
  return out
}

export interface SchemaDraftSpec {
  /** The SCHEMA name, without the version: `asana-authorized-director`. */
  name: string
  /**
   * The SET name, without the version: `asana-authorized-directors`.
   *
   * Separate from the schema name because the registry derives the SET ref from the DATA
   * FILE's name while `$schema_ref` names the schema — in the live fleet these genuinely
   * differ (`asana-sections@v1` satisfies `asana-section@v1`). Collapsing them here would
   * force one spelling and quietly rename half the existing registry's convention.
   */
  setName: string
  version: number
  title: string
  description: string
  /** The collection's property name: `directors`. */
  collection: string
  /** Which field is the stable key every consumer spells (`x-key`). */
  keyField: string
  minItems?: number
  maxItems: number
  /** R7 — which fields record where a record's facts came from. */
  provenanceFields: string[]
  fields: DraftField[]
  /**
   * The set's INITIAL RECORDS, entered at creation.
   *
   * WHY CREATION HAS TO CARRY THEM. The picker's concrete offers
   * (`by_key.<key>.<field>`) are generated from the REAL KEYS in the data
   * (`validate_data_refs.member_keys`), so a set created empty offers no field at
   * all — only the whole-collection `items`. Creating and then filling therefore
   * meant two PRs, two CI runs and a promotion before an author could reference a
   * field, and in between the picker looked broken rather than empty. Seeding here
   * makes the set referenceable the moment it deploys.
   *
   * Values are held in their DECLARED type (number/boolean/string), not as text,
   * so nothing has to be coerced on the way out — a coercion step is where "42"
   * silently becomes a string the schema then rejects.
   */
  rows: Record<string, unknown>[]
}

export interface GeneratedDraft {
  schemaPath: string
  dataPath: string
  schema: Record<string, unknown>
  data: Record<string, unknown>
}

/**
 * The SET name a schema name implies — `asana-section` -> `asana-sections`.
 *
 * WHY THE FORM DERIVES IT INSTEAD OF ASKING
 * ----------------------------------------
 * Every set in the live registry is the plural of its schema, without exception:
 * `asana-authorized-director`/`asana-authorized-directors`, `asana-custom-field`/
 * `asana-custom-fields`, `asana-section`/`asana-sections`, `website-property`/
 * `website-properties`. Asking for a name that has been mechanical four times out of four is
 * asking an operator to restate a convention.
 *
 * IT IS A PREFILL, NOT A RULE. The two names are genuinely separate identities — the registry
 * is keyed by DATA documents (ADR-125 D1) and nothing requires one schema to back one set, so
 * `asana-section@v1` may legitimately back both `triage-sections` and `billing-sections`. The
 * field therefore stays editable, and this only decides what is in it to begin with.
 *
 * English will eventually beat this. That is fine and it is why the result is editable: a wrong
 * guess is visible in the box before anything is created, which is the cheapest possible place
 * for it to be wrong.
 */
export function pluralise(schemaName: string): string {
  const name = schemaName.trim()
  if (!name) return ''
  // `property` -> `properties`, and NOT `propertys`: the only irregular case the live registry
  // actually contains, so it is the one rule worth having beyond a bare `s`.
  if (/[^aeiou]y$/.test(name)) return name.slice(0, -1) + 'ies'
  // `ss`, not a single `s`: `address` -> `addresses`, while a trailing single `s` is taken as
  // ALREADY PLURAL and left alone, so an operator who types the plural into the schema box does
  // not get `asana-sectionses`. The cost is that a genuine singular ending in one `s` (`bus`)
  // comes back unchanged — accepted, because no such schema name exists or is likely, and the
  // field is editable, so the worst case is a name an operator corrects before creating.
  if (/(ss|x|z|ch|sh)$/.test(name)) return name + 'es'
  if (name.endsWith('s')) return name
  return name + 's'
}

/**
 * The COLLECTION property a set name implies — `asana-sections` -> `sections`.
 *
 * WHY THIS IS NOT AN OPERATOR-FACING NAME AT ALL
 * ---------------------------------------------
 * It is a JSON property key inside the schema, and every reader of it DISCOVERS it rather than
 * knowing it: `validate_data_refs.keyed_collection` returns "the property carrying `x-key`, or
 * the sole array". It appears in no reference — a token reads
 * `{{data.<alias>.by_key.<key>.<field>}}`, never `{{data.<alias>.sections.…}}`. So nothing
 * anywhere spells this name, and asking an operator to invent one buys them nothing.
 *
 * Derived from the SET name rather than the schema name because that is the convention in the
 * registry (the plural's last segment), and it stays editable behind the advanced disclosure so
 * an existing artifact can still be corrected.
 */
export function collectionFor(setName: string): string {
  const last = setName.trim().split('-').filter(Boolean).pop() ?? ''
  return last.replace(/[^a-z0-9_]/g, '_')
}

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/

/**
 * The slug a human's typing implies — `Fleet Short Codes`, `fleet_short_codes`
 * and `Fleet-Short-Codes` all become `fleet-short-codes`.
 *
 * WHY THIS EXISTS RATHER THAN A REFUSAL. The registry's naming convention is an
 * implementation detail of the file layout ([ADR-125] D3), and an operator who
 * types `fleet_short_codes` has told us exactly what they mean. Answering that
 * with "must be a lowercase slug" makes them do a mechanical transform the
 * application is perfectly able to do — and it is the kind of friction that
 * teaches people the form is fussy rather than helpful. The refusal in
 * specProblems stays as the backstop for anything unsalvageable (empty, or
 * punctuation-only); it should now be unreachable from ordinary typing.
 */
export function slugify(text: string): string {
  return slugifyTyping(text).replace(/-+$/g, '')
}

/**
 * The same transform, MINUS the trailing-separator trim.
 *
 * Trimming on every keystroke makes the field unusable: typing
 * `website-property` passes through `website-`, and stripping that hyphen
 * immediately turns the next character into `websitep`. So the live control keeps
 * a trailing separator and `slugify` removes it once, where the canonical value
 * is taken (validation and generation both normalise, so state can hold an
 * in-progress spelling without producing an in-progress artifact).
 */
export function slugifyTyping(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/g, '')
}

/** The collection property's spelling — same idea, but underscores (see collectionFor). */
export function slugifyCollection(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * A blank trailing field or record row is the form's ADD affordance, not a
 * declaration the operator made. Dropping them before validating is what stops
 * "A field has no name." appearing the instant someone clicks "add field" —
 * an error about something they have not done yet.
 */
function pruneFields(fields: DraftField[]): DraftField[] {
  return fields
    .filter(f => f.name.trim() !== '')
    .map(f => (f.type === 'object' ? { ...f, fields: pruneFields(f.fields ?? []) } : f))
}

export function withoutBlankRows(spec: SchemaDraftSpec): SchemaDraftSpec {
  const fields = pruneFields(spec.fields)
  const rows = spec.rows.filter(row =>
    Object.values(row).some(v => v !== undefined && v !== null && v !== ''))
  // Names are canonicalised HERE rather than in the input handler, so an
  // in-progress `website-` never becomes an in-progress artifact.
  return { ...spec, name: slugify(spec.name), setName: slugify(spec.setName), fields, rows }
}

/**
 * Refusals, in the order an operator would hit them. Each one is a draft that CANNOT be made
 * well-formed, as distinct from one the validator might reject for a deeper reason.
 */
export function specProblems(input: SchemaDraftSpec): string[] {
  return [...schemaProblems(input), ...recordProblems(input)]
}

/**
 * Everything wrong with the SHAPE — the half that must be right before first
 * records mean anything. Split out because a record cannot be judged against a
 * schema that does not yet exist: asking for rows while the key field is
 * unresolved produces errors about the rows that are really errors about the
 * schema.
 */
export function schemaProblems(input: SchemaDraftSpec): string[] {
  const spec = withoutBlankRows(input)
  const problems: string[] = []
  if (!SLUG.test(spec.name)) {
    problems.push('The schema name must be a lowercase slug, e.g. asana-authorized-director.')
  }
  if (!Number.isInteger(spec.version) || spec.version < 1) {
    problems.push('The version must be a whole number, 1 or greater.')
  }
  if (!SLUG.test(spec.setName)) {
    problems.push('The set name must be a lowercase slug, e.g. asana-authorized-directors.')
  }
  if (!/^[a-z0-9_]+$/.test(spec.collection)) {
    problems.push('The collection name must be lowercase letters, digits or underscores, e.g. directors.')
  }
  if (spec.fields.length === 0) {
    problems.push('A record needs at least one field.')
  }
  const siblingsUnique = (fields: DraftField[], where: string): void => {
    const named = fields.map(f => f.name)
    if (new Set(named).size !== named.length) {
      problems.push(`Two fields share a name${where}.`)
    }
    for (const f of fields) {
      if (f.type !== 'object') continue
      const children = f.fields ?? []
      if (children.length === 0) {
        // An object with no children is not a shape — it validates every object
        // and describes none, so a consumer's `.address.city` cannot be checked.
        problems.push(`"${f.name}" is an object with no fields. Add its fields, or make it a value.`)
      }
      siblingsUnique(children, ` inside "${f.name}"`)
    }
  }
  siblingsUnique(spec.fields, '')
  // The key and the provenance fields name LEAVES, spelled the way a consumer
  // spells them (`address.city`). An object is not a value, so it cannot be either.
  const names = leafPaths(spec.fields)
  if (!names.includes(spec.keyField)) {
    // x-key names the field every consumer spells as `by_key.<value>`. Pointing it at a field
    // that does not exist produces a set nothing can address.
    problems.push(`The key field "${spec.keyField}" is not one of this record's fields.`)
  }
  for (const p of spec.provenanceFields) {
    if (!names.includes(p)) {
      problems.push(`Provenance field "${p}" is not one of this record's fields.`)
    }
  }
  if (spec.minItems !== undefined && spec.minItems > spec.maxItems) {
    problems.push('The minimum cannot exceed the maximum.')
  }
  for (const f of spec.fields) {
    if (f.unknownSentinel !== undefined && f.unknownSentinel !== '' && f.type === 'string') {
      // A sentinel on a string field is indistinguishable from an ordinary value, so the
      // type-preserving oneOf buys nothing and the "unknown" state becomes unreadable.
      problems.push(
        `"${f.name}" is a string, so an unknown-sentinel cannot be told apart from a real ` +
        `value — use it on a non-string field, or model the unknown state as its own field.`)
    }
  }
  return problems
}

/** Everything wrong with the FIRST RECORDS, judged against a shape already known good. */
export function recordProblems(input: SchemaDraftSpec): string[] {
  const spec = withoutBlankRows(input)
  const problems: string[] = []
  const keyValues = spec.rows
    .map(row => readPath(row, spec.keyField))
    .filter(value => value !== undefined && value !== '')
  const duplicates = keyValues.filter((value, i) => keyValues.indexOf(value) !== i)
  if (duplicates.length > 0) {
    // The canonical validator catches this too (`check_key_uniqueness`), and it is
    // authoritative. Repeated here ONLY because it can name the offending value
    // while the operator is still typing, rather than after a round trip.
    problems.push(
      `Two records share the key ${JSON.stringify(duplicates[0])}. A key that repeats is not a key `
      + `— every consumer addressing it would get whichever row won.`)
  }
  return problems
}

function fieldSchema(f: DraftField): Record<string, unknown> {
  if (f.type === 'object') {
    const children = (f.fields ?? []).filter(c => c.name.trim() !== '')
    const properties: Record<string, unknown> = {}
    for (const c of children) properties[c.name] = fieldSchema(c)
    const node: Record<string, unknown> = { type: 'object', properties }
    const required = children.filter(c => c.required).map(c => c.name)
    if (required.length > 0) node.required = required
    if (f.description) node.description = f.description
    if (f.deprecatedOn) node['x-deprecated'] = { since: f.deprecatedOn }
    return node
  }
  const base: Record<string, unknown> = { type: f.type }
  if (f.description) base.description = f.description
  if (f.unit) base['x-unit'] = f.unit
  if (f.deprecatedOn) base['x-deprecated'] = { since: f.deprecatedOn }
  if (f.unknownSentinel) base['x-unknown-sentinel'] = f.unknownSentinel
  return base
}

/**
 * The schema and the first data document, ready to propose as one changeset.
 *
 * The data document is created with an EMPTY collection unless a minimum forces otherwise, in
 * which case one template record is seeded — because a document that violates its own
 * schema's `minItems` on creation would be a draft the validator rejects for a reason the
 * operator did not choose.
 */
export function generateDraft(input: SchemaDraftSpec): GeneratedDraft {
  const spec = withoutBlankRows(input)
  const record: Record<string, unknown> = {}
  for (const f of spec.fields) record[f.name] = fieldSchema(f)

  const collection: Record<string, unknown> = {
    type: 'array',
    maxItems: spec.maxItems,
    'x-key': spec.keyField,
    items: {
      type: 'object',
      properties: record,
      required: spec.fields.filter(f => f.required).map(f => f.name),
    },
  }
  if (spec.minItems !== undefined) collection.minItems = spec.minItems
  if (spec.provenanceFields.length > 0) {
    collection['x-provenance'] = { fields: [...spec.provenanceFields] }
  }

  const setRef = `${spec.name}@v${spec.version}`
  const schema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: setRef,
    title: spec.title || spec.name,
    description: spec.description,
    status: 'published',
    // A fleet may only author a fleet-owned schema: ADR-125 D10 says core never prescribes a
    // fleet's operational values, and D3 puts core artifacts in a namespace this surface is
    // refused write access to anyway. So there is no owner picker — offering one would be
    // offering a choice that cannot be saved.
    owner: 'fleet',
    type: 'object',
    properties: { [spec.collection]: collection },
    required: [spec.collection],
  }

  // The operator's rows win. A BLANK is omitted rather than written as `""`/0:
  // an empty string is a value, and it would satisfy `required` while failing a
  // pattern — so the validator's "required missing" is the clearer answer.
  const authored = spec.rows
    .map(row => {
      const record: Record<string, unknown> = {}
      for (const f of spec.fields) {
        const value = row[f.name]
        if (value === undefined || value === '') continue
        record[f.name] = value
      }
      return record
    })
    .filter(record => Object.keys(record).length > 0)

  const seed: Record<string, unknown>[] = [...authored]
  // Only when the operator supplied nothing: a document that violates its own
  // schema's minItems on creation is a draft the validator rejects for a reason
  // the operator did not choose.
  if (seed.length === 0 && (spec.minItems ?? 0) > 0) {
    const row: Record<string, unknown> = {}
    for (const f of spec.fields) {
      if (!f.required) continue
      row[f.name] = f.type === 'boolean' ? false : f.type === 'string' ? '' : 0
    }
    for (let i = 0; i < (spec.minItems ?? 0); i++) seed.push({ ...row })
  }

  return {
    // The registry derives the SET ref from the data file's name, so `setName` is what
    // `<set>@v<N>` will be — and `$schema_ref` below points at the SCHEMA's own ref.
    schemaPath: `config/schemas/${spec.name}/v${spec.version}.json`,
    dataPath: `config/data/${spec.setName}.v${spec.version}.json`,
    schema,
    data: {
      $schema_ref: setRef,
      owner: 'fleet',
      [spec.collection]: seed,
    },
  }
}
